import React from 'react';
import { Play, Loader2, Square } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import type { ToolKey } from '../../api/premiumService';
import { resultsRegistry } from './Results/registry';

type Props = {
  title: string;
  desc: string;
  Icon: React.ComponentType<any>;
  isPremium: boolean;
  running: boolean;
  input: string;
  setInput: (v: string) => void;
  onRun: () => void;
  onCancel: () => void;     // nuevo
  canCancel: boolean;       // nuevo
  results: any[];
  activeTool: ToolKey;
  errorMsg: string | null;
  placeholder: string;

  // ✅ NUEVO: contenido que se renderiza inmediatamente a la derecha del botón principal
  rightActionAddon?: React.ReactNode;
};

const fadeUp = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
  transition: { duration: 0.22 },
};

const SLOW_HINT_MS = 15000;

const ToolPanel: React.FC<Props> = ({
  title, desc, Icon, isPremium, running, input, setInput, onRun, onCancel, canCancel,
  results, activeTool, errorMsg, placeholder, rightActionAddon,
}) => {
  const { t } = useTranslation();
  const Renderer = resultsRegistry[activeTool];

  const [showSlowHint, setShowSlowHint] = React.useState(false);
  React.useEffect(() => {
    let timer: number | undefined;
    if (running) {
      setShowSlowHint(false);
      timer = window.setTimeout(() => setShowSlowHint(true), SLOW_HINT_MS);
    } else {
      setShowSlowHint(false);
    }
    return () => { if (timer) window.clearTimeout(timer); };
  }, [running]);

  // Hover para cambiar a "Cancelar"
  const [hover, setHover] = React.useState(false);

  const runLabel = running
    ? (hover && canCancel ? t('tools.cancel') : t('tools.running'))
    : t('tools.run');

  const buttonColors = running && hover && canCancel
    ? 'bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700'
    : (isPremium
        ? 'bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-700 hover:to-indigo-700'
        : 'bg-slate-700 hover:bg-slate-800');

  // 🔒 asegúrate de pintar string aunque errorMsg no lo sea (cinturón y tirantes)
  const renderError = (msg: any) => {
    if (!msg) return null;
    if (typeof msg === 'string') return msg;
    try { return JSON.stringify(msg); } catch { return String(msg); }
  };

  return (
    <section className="relative rounded-2xl p-8 shadow-sm border border-slate-200 bg-white flex flex-col items-center text-center">
      {/* Header */}
      <div className="flex flex-col items-center mb-6">
        <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-sky-600 to-indigo-600 text-white flex items-center justify-center shadow-md mb-3">
          <Icon className="h-7 w-7" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 tracking-tight">{title}</h2>
        <p className="mt-1 text-sm text-slate-600 max-w-md">{desc}</p>
      </div>

      {errorMsg && (
        <div className="mb-4 w-full max-w-lg rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900 text-center">
          {renderError(errorMsg)}
        </div>
      )}

      {/* Input + acciones */}
      <div className="w-full max-w-lg flex flex-col sm:flex-row gap-3 justify-center mb-6">
        <input
          className="flex-1 border border-slate-300 bg-white rounded-lg px-3 py-2
                     focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent
                     placeholder:text-slate-400"
          placeholder={placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />

        <div className="flex items-center gap-2">
          <button
            type="button"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onClick={() => (running && canCancel ? onCancel() : onRun())}
            // importante: NO deshabilitar cuando está running (para poder cancelar)
            disabled={!isPremium}
            className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-white font-semibold transition ${buttonColors} ${!isPremium ? 'opacity-60 cursor-not-allowed' : ''}`}
            title={running && canCancel ? (t('tools.cancel') as string) : (t('tools.run') as string)}
          >
            {running
              ? (hover && canCancel ? <Square className="h-4 w-4" /> : <Loader2 className="h-4 w-4 animate-spin" />)
              : <Play className="h-4 w-4" />
            }
            {runLabel}
          </button>

          {/* ✅ Addon a la derecha del botón (selector, etc.) */}
          {rightActionAddon ? (
            <div className="inline-flex">{rightActionAddon}</div>
          ) : null}
        </div>
      </div>

      {/* Separador */}
      <hr className="w-full max-w-6xl border-t border-slate-200 mb-6" />

      {/* Resultados / Loading */}
      <div className="w-full flex justify-center">
        <AnimatePresence mode="wait">
          {running ? (
            <motion.div key="loading" {...fadeUp} className="flex flex-col items-center justify-center py-10" aria-live="polite" aria-busy="true">
              <Loader2 className="h-10 w-10 text-sky-600 animate-spin" />
              {showSlowHint && (
                <div className="mt-3 text-sm text-slate-500 animate-pulse">
                  {t('tools.slow_loading')}
                </div>
              )}
            </motion.div>
          ) : !Renderer ? (
            <motion.p key="no-renderer" {...fadeUp} className="text-sm text-rose-500">
              {t('tools.no_renderer')}
            </motion.p>
          ) : results.length === 0 ? (
            <motion.p key="noresults" {...fadeUp} className="text-sm text-slate-500">
              {t('tools.no_results')}
            </motion.p>
          ) : (
            <motion.div key="results" {...fadeUp} className="w-full max-w-full lg:max-w-6xl text-left">
              <Renderer results={results} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default ToolPanel;
